import "react-notifications-component/dist/theme.css";
import "../index.css";

import { ConfirmationModal, FullScreenLoader } from "./components";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  filterBasedOnUserGroups,
  findDefaultPage,
  getUrlParameter,
  isFrontlyAdmin,
  publicRoutes,
} from "./utils/utils";
import { get, isEmpty } from "lodash";
import {
  rAdminPreview,
  rApp,
  rApps,
  rForms,
  rIsFetchingConfig,
  rIsIntercomBooted,
  rIsMixpanelInitialized,
  rOrganization,
  rPages,
  rUser,
} from "./utils/recoil";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import AdminLogin from "./adminApp/pages/AdminLogin";
import AiBuilder from "./adminApp/aibuilder";
import AiView from "./adminApp/aiview";
import AppSumoModal from "./adminApp/AppSumoModal";
import AppSumoSignup from "./adminApp/pages/AppSumoSignup";
import Apps from "./adminApp/home/Apps";
import Assets from "./adminApp/assets";
import Cookies from "js-cookie";
import CustomModal from "./renderPage/CustomModal";
import CustomerPasswordReset from "./adminApp/settings/CustomerPasswordReset";
import DataSourceDetails from "./adminApp/spreadsheets/DataSourceDetails";
import EditActiveUser from "./adminApp/settings/EditActiveUser";
import EmailVerification from "./adminApp/EmailVerification";
import FormEditor from "./adminApp/forms/FormEditor";
import Forms from "./adminApp/forms";
import FullScreenForm from "./renderingApp/blocks/FullScreenForm";
import FullScreenLoaderClient from "./components/FullScreenLoaderClient";
import Home from "./adminApp/home";
import InternalAdmin from "./adminApp/internalAdmin";
import IntroModal from "./adminApp/IntroModal";
import Listing from "./adminApp/settings/marketplace/Listing";
import ListingDetails from "./adminApp/settings/marketplace/ListingDetails";
import Login from "./adminApp/pages/Login";
import Marketplace from "./adminApp/settings/marketplace/Home";
import MarketplaceAdmin from "./adminApp/settings/marketplace/MarketplaceAdmin";
import NewTemplates from "./adminApp/new_templates/NewTemplates";
import Onboarding from "./adminApp/Onboarding";
import PageBuilderV2 from "./adminApp/pageBuilder/v2";
import Pages from "./adminApp/home/Pages";
import PrivateRoute from "./PrivateRoute";
import ReactGA from "react-ga4";
import { ReactNotifications } from "react-notifications-component";
import RefreshSheetModal from "./adminApp/components/RefreshSheetModal";
import RenderPage from "./renderPage";
import Settings from "./adminApp/settings";
import Spreadsheets from "./adminApp/spreadsheets";
import TagManager from "react-gtm-module";
import TemplateDetails from "./adminApp/new_templates/TemplateDetails";
import UsersList from "./adminApp/users/UsersList";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useBranding } from "./useBranding";
import useFetchAppConfig from "./utils/useFetchAppConfig";
import { useIntercom } from "react-use-intercom";
import useModalStateData from "./useModalStateData";
import useUtils from "./renderingApp/useUtils";
import useWebsocket from "./useWebsocket";

function App() {
  const user = useRecoilValue(rUser);
  const organization = useRecoilValue(rOrganization);
  const app = useRecoilValue(rApp);
  const savedForms = useRecoilValue(rForms);
  const savedPages = useRecoilValue(rPages);
  const apps = useRecoilValue(rApps);
  const isFetchingConfig = useRecoilValue(rIsFetchingConfig);

  const fetchAppConfig = useFetchAppConfig();

  const { boot } = useIntercom();

  const appCount = apps.length;

  const [showIntroModal, setShowIntroModal] = useState(true);
  const [showAppSumoModal, setShowAppSumoModal] = useState(true);

  const { passesDisplayConditions } = useUtils();

  const location = useLocation();

  useEffect(() => {
    if (isFrontlyAdmin) {
      const pagePath = location.pathname + location.search;
      TagManager.dataLayer({
        dataLayer: {
          event: "pageview",
          pagePath: pagePath,
        },
      });
    }
  }, [location]);

  useBranding();
  useWebsocket();

  const forms = savedForms.filter(
    (f) =>
      f.published &&
      passesDisplayConditions({ conditions: get(f, "displayConditions", []) })
  );

  const publicForms = forms.filter((p) => !get(p, ["requireLogin"]));
  const privateForms = forms.filter((p) => get(p, ["requireLogin"]));

  const pages = savedPages
    .filter(
      (p) => p.published || ["owner", "admin"].includes(get(user, "role"))
    )
    .filter((p) =>
      passesDisplayConditions({ conditions: get(p, "displayConditions", []) })
    );

  const publicPages = pages.filter((p) => !get(p, ["requireLogin"]));

  const privatePages = filterBasedOnUserGroups({
    items: pages.filter((p) => get(p, ["requireLogin"])),
    app,
    user,
    passesDisplayConditions,
  });

  const defaultPageId = get(app, "default_page");

  const defaultForm = publicForms.find((f) => f.makeDefaultPage);

  const defaultPrivatePage = privatePages.find((p) => p.id === defaultPageId);

  const defaultPublicPage = publicPages.find((p) => p.id === defaultPageId);

  const setIsMixpanelInitialized = useSetRecoilState(rIsMixpanelInitialized);

  const [adminPreview, setAdminPreview] = useRecoilState(rAdminPreview);

  const [isIntercomBooted, setIsIntercomBooted] =
    useRecoilState(rIsIntercomBooted);

  const path = get(location, "pathname");

  const subscription = get(organization, "subscription");
  const currentPlan = get(subscription, "plan");
  const planPrice = get(subscription, "amount");

  const isPublicRoute =
    publicRoutes.includes(path) ||
    (isFrontlyAdmin && path.includes("/expert/")) ||
    (isFrontlyAdmin && path.includes("/templates"));

  const showIntroModalPopup =
    !path.includes("billing") &&
    !path.includes("onboarding") &&
    !isPublicRoute &&
    showIntroModal &&
    !currentPlan &&
    isFrontlyAdmin &&
    !Cookies.get("hideIntroModal");

  const planCreatedDate = get(subscription, "created_date");

  const isWithinLastWeek = moment(planCreatedDate).isAfter(
    moment().subtract(1, "week")
  );

  const showAppSumoModalPopup =
    !path.includes("billing") &&
    !path.includes("onboarding") &&
    !isPublicRoute &&
    isFrontlyAdmin &&
    showAppSumoModal &&
    !Cookies.get("hideAppSumoPlanModal") &&
    get(subscription, "plan", "").includes("appsumo") &&
    get(subscription, "id", 0) > 5186 &&
    isWithinLastWeek;

  const hideIntroModal = () => {
    setShowIntroModal(false);
    Cookies.set("hideIntroModal", true, { expires: 7 });
  };

  useEffect(() => {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN);
    setIsMixpanelInitialized(true);

    if (isFrontlyAdmin) {
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID);
      const via = getUrlParameter("via", location);
      if (via) {
        Cookies.set("via", via, { expires: 90 });
      }

      const ref = getUrlParameter("ref", location);
      if (ref) {
        Cookies.set("ref", ref, { expires: 90 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const localhost = window.location.host.includes("localhost");

    if (
      adminPreview === false &&
      !localhost &&
      !isIntercomBooted &&
      user &&
      get(user, "first_name") &&
      window.location.host.includes("app.frontly.ai")
    ) {
      setIsIntercomBooted(true);

      boot({
        userId: user.id,
        name: `${user.first_name} ${user.last_name}`,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        customAttributes: {
          app_count: appCount,
          // plan stuff
          subscription_created_date: get(subscription, "created_date"),
          subscription_updated_date: get(subscription, "updated_date"),
          subscription_cancelled_date: get(subscription, "cancelled_date"),
          team_support: get(subscription, "team_support"),
          plan_name: currentPlan,
          plan_price: planPrice,
          //
          referral_source: organization.referral_source,
          organization: organization.id,
          ai_usage: get(organization, "ai_usage", 0),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get(user, "id")]);

  const navigate = useNavigate();

  const magicLink = getUrlParameter("magic", location);
  const magicRoute = magicLink && getUrlParameter("route", location);
  const magicAppId = magicLink && getUrlParameter("app_id", location);

  // COMMENTING OUT FOR NOW, BUT SHOULD IMPLEMENT FOR DARK MODE

  // Preparing for more theme styles
  // const stylingObject = {
  //   "--primary": primaryColor,
  // };

  // useEffect(() => {
  //   // Dynamically update CSS variable
  //   Object.keys(stylingObject).forEach((key) => {
  //     const value = stylingObject[key];
  //     if (value) {
  //       document.documentElement.style.setProperty(key, value);
  //     }
  //   });
  //   // theme === "dark" || isAdminPublicPage;
  //   // document.body.classList.toggle("dark-mode", true);
  // }, [primaryColor]);

  useEffect(() => {
    const adminPreviewParam = getUrlParameter("admin_preview", location);

    const adminPreviewCookie = Cookies.get("adminPreview");
    if (adminPreviewParam === "true" || adminPreviewCookie) {
      setAdminPreview(true);
      if (!adminPreviewCookie) {
        Cookies.set("adminPreview", true, { expires: 30 });
      }
    } else {
      setAdminPreview(false);
    }

    if (magicLink) {
      Cookies.set("accessToken", magicLink, { expires: 30 });

      if (magicRoute && magicAppId) {
        navigate(`${magicRoute}?app_id=${magicAppId}`);
      } else if (magicRoute) {
        navigate(magicRoute);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle when you have magic link, but no specific route defined, send to first page
  useEffect(() => {
    if (user) {
      if (magicLink && magicRoute === "") {
        if (user.role === "user") {
          const fallback = get(savedPages, 0, {});
          const defaultPage =
            savedPages.find((p) => p.id === get(app, "default_page")) ||
            fallback;
          const userGroups = get(app, "user_groups");

          const defPage = findDefaultPage({
            user,
            userGroups,
            pages: savedPages,
            defaultPage,
            app,
            passesDisplayConditions,
          });

          navigate(defPage);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get(user, "id")]);

  const [customJsRun, setCustomJsRun] = useState(false);

  const { setModalStack, modalStack } = useModalStateData();

  // Fetch app config
  useEffect(() => {
    const r = fetchAppConfig({});

    // This ensures that the modal stack is cleared when you navigate to a new page
    if (modalStack.length > 0) {
      setModalStack([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const getFinalRoute = (page) => {
    const route = get(page, "route");
    return route;
  };

  useEffect(() => {
    if (!isPublicRoute && isFrontlyAdmin && !Cookies.get("accessToken")) {
      navigate("/login");
    }
  }, []);

  const showEmailVerification = get(app, "email_verification_required", false);

  const showSpinner =
    isFetchingConfig || ((!isPublicRoute || !isFrontlyAdmin) && isEmpty(app));

  const allowCustomJavascript = get(organization, "allow_custom_javascript");

  // JS
  useEffect(() => {
    if (
      allowCustomJavascript &&
      !showSpinner &&
      !isFrontlyAdmin &&
      !customJsRun &&
      !isEmpty(app)
    ) {
      setCustomJsRun(true);

      // Extract or get the JavaScript code from `activeApp`
      const customJs = get(app, "custom_javascript", "");

      if (customJs) {
        // Match all <script> tags, including those with src attributes
        const scriptMatches = customJs.matchAll(
          /<script.*?>[\s\S]*?<\/script>/gi
        );
        const scripts = [...scriptMatches];

        if (scripts.length > 0) {
          scripts.forEach((match) => {
            const scriptContent = match[0];
            const scriptElement = document.createElement("script");
            const scriptTagMatch = scriptContent.match(
              /<script.*?>([\s\S]*?)<\/script>/i
            );
            const scriptSrcMatch = scriptContent.match(/src="([^"]+)"/i);

            if (scriptSrcMatch && scriptSrcMatch[1].trim()) {
              // External JavaScript source
              // console.log("External script:", scriptSrcMatch[1]);
              scriptElement.src = scriptSrcMatch[1];
              if (scriptContent.includes("async")) scriptElement.async = true;
            } else if (scriptTagMatch && scriptTagMatch[1].trim()) {
              // Inline JavaScript code
              // console.log("Inline JS code");
              scriptElement.innerHTML = scriptTagMatch[1];
            } else {
              // This case is unlikely due to the regex match, but included for completeness
              // console.log("Plain JS or unmatched case", scriptContent);
              scriptElement.innerHTML = scriptContent;
            }

            scriptElement.type = "text/javascript";
            document.head.appendChild(scriptElement);
          });
        } else {
          // Handle case where there are no <script> tags, assuming plain JavaScript
          // console.log("Plain JavaScript without <script> tags", customJs);
          const scriptElement = document.createElement("script");
          scriptElement.innerHTML = customJs;
          scriptElement.type = "text/javascript";
          document.head.appendChild(scriptElement);
        }

        // Clean up script elements when the component unmounts
        return () => {
          document
            .querySelectorAll("script[data-custom-js='true']")
            .forEach((script) => {
              document.head.removeChild(script);
            });
        };
      }
    }
  }, [app]);

  if (showEmailVerification) {
    return <EmailVerification />;
  }

  if (showSpinner) {
    return <FullScreenLoader />;
  }

  const renderDefaults = () => {
    if (defaultForm) {
      return (
        <Route path={""} element={<FullScreenForm data={defaultForm} />} />
      );
    } else if (defaultPublicPage) {
      return (
        <Route
          path=""
          element={<RenderPage page={defaultPublicPage} isPublicRoute />}
        />
      );
    }

    return <Route path="" element={<Login type="login" />} />;
  };

  return (
    <div>
      <CustomModal page={{}} />
      {showIntroModalPopup && <IntroModal hide={hideIntroModal} />}
      {showAppSumoModalPopup && (
        <AppSumoModal
          hide={() => {
            setShowAppSumoModal(false);
            Cookies.set("hideAppSumoPlanModal", true, { expires: 30 });
          }}
        />
      )}
      <div id="dropdown-portal-root" className="test"></div>
      <ReactNotifications style={{ zIndex: 2000 }} />
      <FullScreenLoaderClient />
      <AiView />
      {isFrontlyAdmin && <RefreshSheetModal />}
      <Routes>
        <Route path="templates" element={<NewTemplates />} />
        <Route path="templates/:id" element={<TemplateDetails />} />
        {isFrontlyAdmin && (
          <>
            <Route path="internal_admin" element={<InternalAdmin />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="forms" element={<Forms />} />
            <Route path="form/:id" element={<FormEditor />} />
            <Route path="page/:id" element={<PageBuilderV2 />} />
            <Route path="custom-block/:id" element={<PageBuilderV2 />} />
            <Route path="pages" element={<Pages />} />
            <Route path="apps" element={<Apps />} />

            <Route path="aibuilder" element={<AiBuilder />} />

            <Route path="home" element={<Home />} />
            <Route path="spreadsheets" element={<Spreadsheets />} />
            <Route path="spreadsheet/:id" element={<DataSourceDetails />} />
            <Route path="users" element={<UsersList />} />
            <Route path="settings/:id" element={<Settings />} />
            <Route path="settings" element={<Settings />} />
            <Route path="assets" element={<Assets />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route
              path="marketplace/profile/:builderId"
              element={<Marketplace />}
            />
            <Route
              path="marketplace_admin/:tab"
              element={<MarketplaceAdmin />}
            />
            <Route
              path="marketplace_admin/listing/:id"
              element={<ListingDetails />}
            />
            <Route path="marketplace/listing/:id" element={<Listing />} />

            <Route path="" element={<AdminLogin />} />
            <Route path="login" element={<AdminLogin type="login" />} />
            <Route path="signup" element={<AdminLogin type="signup" />} />
            <Route path="appsumo-signup" element={<AppSumoSignup />} />
          </>
        )}

        <Route path="verify-email" element={<EmailVerification />} />

        <Route path="fullscreenform" element={<FullScreenForm />} />
        {!isFrontlyAdmin && (
          <>
            {renderDefaults()}

            {get(app, "enable_edit_profile", false) && (
              <Route path="edit-profile" element={<EditActiveUser />} />
            )}

            <Route path="login" element={<Login type="login" />} />
            {get(app, ["authentication", "enableSignup"]) && (
              <Route path="signup" element={<Login type="signup" />} />
            )}

            {publicForms.map((f) => {
              return (
                <Route
                  key={get(f, "route")}
                  path={get(f, "route")}
                  element={<FullScreenForm data={f} />}
                />
              );
            })}

            {/* USER ROUTES */}
            {publicPages.map((page) => {
              let route = getFinalRoute(page);
              return (
                <Route
                  key={route}
                  path={route}
                  element={<RenderPage page={page} isPublicRoute />}
                />
              );
            })}
            <Route path="/" element={<PrivateRoute />}>
              <Route
                path="/reset_password"
                element={<CustomerPasswordReset />}
              />
              {privateForms.map((f) => {
                return (
                  <Route
                    key={get(f, "route")}
                    path={get(f, "route")}
                    element={<FullScreenForm data={f} />}
                  />
                );
              })}
              {privatePages.map((page) => {
                let route = getFinalRoute(page);
                return (
                  <Route
                    key={route}
                    path={route}
                    element={<RenderPage page={page} />}
                  />
                );
              })}

              {/* IF THERE IS A DEFAULT PRIVATE PAGE, RENDER THAT */}
              {defaultPrivatePage && (
                <Route
                  path="*"
                  element={<RenderPage page={defaultPrivatePage} />}
                />
              )}
            </Route>
          </>
        )}

        {/* IF THERE IS A DEFAULT PUBLIC PAGE, RENDER THAT */}
        {!defaultPrivatePage && defaultPublicPage && (
          <Route
            path="*"
            element={<RenderPage page={defaultPublicPage} isPublicRoute />}
          />
        )}

        {/* IF NO DEFAULT PRIVATE OR PUBLIIC PAGES ACCESSIBLE, RENDER LOGIN */}
        {!defaultPrivatePage && !defaultPublicPage && (
          <Route path="*" element={<Login type="login" />} />
        )}
      </Routes>
      <ConfirmationModal />
    </div>
  );
}

export default App;
